import { ActionIcon, CloseButton, Group, Portal } from '@mantine/core';
import { useToggle } from '@mantine/hooks';
import { IconArrowsDiagonal, IconSparkles } from '@tabler/icons-react';
import { Link, useLocation } from '@tanstack/react-router';
import { AnimatePresence, motion } from 'motion/react';
import { use } from 'react';

import type { CoreRouter } from '#main';

import classes from './SidebarAtlasAi.module.css';
import { Chat } from '#components/chat/Chat';
import { ChatProvider } from '#components/chat/contexts/ChatProvider';
import { MenuLink } from '#components/layout/MenuLink';
import type { CustomSidebarEntryProps } from '#components/layout/Sidebar';
import { ProjectContext } from '#contexts/ProjectContext';
import { useChatSession } from '#hooks/useChatSession';

export function SidebarAtlasAi({ portalTarget }: CustomSidebarEntryProps) {
  const { config } = use(ProjectContext);
  const session = useChatSession();
  const [isAiOpen, toggleIsAiOpen] = useToggle();
  const pathname: string = useLocation<CoreRouter, string, boolean>({ select: ({ pathname }) => pathname });

  if (!config.feature.ai.enabled) {
    return null;
  }

  if (pathname.startsWith('/ai') && isAiOpen) {
    toggleIsAiOpen(false);
  }

  return (
    <>
      <MenuLink
        icon={IconSparkles}
        to={session?.session?.id ? '/ai/$chatSessionId' : '/ai'}
        params={{ chatSessionId: session?.session?.id }}
        onClick={(ev) => {
          // TODO: Handle in router
          if (!isAiOpen && !pathname.startsWith('/ai')) {
            ev.preventDefault();
            ev.stopPropagation();
            toggleIsAiOpen(true);
            return;
          }
        }}
        title="Cognite Atlas AI™"
      />

      <Portal target={portalTarget.current ?? undefined}>
        <AnimatePresence>
          {isAiOpen && !pathname.startsWith('/ai') && (
            <motion.div
              animate={{ width: '50dvw' }}
              className={classes.chat}
              exit={{ width: '0dvw' }}
              initial={{ width: '0dvw' }}
            >
              <div className={classes.chatContents}>
                <div className={classes.chatHeader}>
                  <div>Cognite Atlas AI™</div>
                  <Group gap="sm">
                    <Link
                      to={session?.session?.id ? '/ai/$chatSessionId' : '/ai'}
                      params={{ chatSessionId: session?.session?.id }}
                    >
                      <ActionIcon color="white" variant="subtle" onClick={() => toggleIsAiOpen(true)}>
                        <IconArrowsDiagonal size={16} />
                      </ActionIcon>
                    </Link>
                    <CloseButton onClick={() => toggleIsAiOpen(false)} />
                  </Group>
                </div>

                <ChatProvider session={session}>
                  <Chat />
                </ChatProvider>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </Portal>
    </>
  );
}
